
import React, { useState, useMemo, useRef } from 'react';
import { 
  DollarSign, TrendingUp, TrendingDown, 
  Search, BarChart3, ChevronRight, X, Printer, 
  Fuel, Target,
  Receipt, PackagePlus, ArrowUpRight, ArrowDownLeft, 
  MapPin, Star, Plus, Trash2, Save, Edit3,
  Tag, ShieldAlert, Camera, Link, Check as CheckIcon
} from 'lucide-react';
import { Order, Client, ExpenseStatus, ExtraItemStatus, EventExpense, ExtraItem } from './types';
import { Card, StatCard } from './SharedComponents';

export const EventProfitabilityPage = ({ orders, clients, onUpdateOrders }: { orders: Order[], clients: Client[], onUpdateOrders: (orders: Order[]) => void }) => {
  const [search, setSearch] = useState('');
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);

  const stats = useMemo(() => {
    const totalRev = orders.reduce((acc, o) => {
      const approvedExtras = o.extraItems?.filter(e => e.status === ExtraItemStatus.APROVADO).reduce((a,e) => a + (e.qty*e.value), 0) || 0;
      return acc + o.estimatedCost + approvedExtras;
    }, 0);

    const totalExp = orders.reduce((acc, o) => {
      const approvedExpenses = o.expenses?.filter(e => e.status === ExpenseStatus.APROVADO).reduce((a,e) => a + e.amount, 0) || 0;
      return acc + approvedExpenses;
    }, 0);

    return {
      revenue: totalRev,
      costs: totalExp,
      profit: totalRev - totalExp,
      margin: totalRev > 0 ? ((totalRev - totalExp) / totalRev) * 100 : 0
    };
  }, [orders]);

  const filteredOrders = orders.filter(o => 
    o.nomeEvento.toLowerCase().includes(search.toLowerCase()) ||
    o.id.toLowerCase().includes(search.toLowerCase())
  );

  const handleUpdateSingleOrder = (updatedOrder: Order) => {
    const newList = orders.map(o => o.id === updatedOrder.id ? updatedOrder : o);
    onUpdateOrders(newList);
    setSelectedOrder(updatedOrder);
  };

  return (
    <div className="space-y-8 animate-in fade-in duration-500 pb-20 p-4 md:p-8 lg:p-10">
      <div className="flex justify-between items-end px-2">
        <div>
          <h1 className="text-3xl font-black text-secondary uppercase tracking-tighter italic">Financeiro por Evento</h1>
          <p className="text-gray-400 text-sm font-medium italic">Análise de margem real após custos de campo aprovados.</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <StatCard title="Faturamento Total" value={`R$ ${stats.revenue.toLocaleString('pt-BR')}`} icon={DollarSign} colorClass="bg-primary/10 text-primary" />
        <StatCard title="Custos Totais" value={`R$ ${stats.costs.toLocaleString('pt-BR')}`} icon={TrendingDown} colorClass="bg-red-50 text-red-600" />
        <StatCard title="Lucro Líquido" value={`R$ ${stats.profit.toLocaleString('pt-BR')}`} icon={TrendingUp} colorClass="bg-green-50 text-green-600" />
        <StatCard title="Margem Média" value={`${stats.margin.toFixed(1)}%`} icon={Target} colorClass="bg-secondary text-white" />
      </div>

      <Card className="overflow-hidden border-none shadow-xl bg-white rounded-[40px]">
        <div className="p-8 border-b border-slate-50 flex flex-col md:flex-row justify-between items-center gap-4">
          <h3 className="text-[10px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-2">
            <BarChart3 size={14} className="text-primary" /> Histórico de O.S.
          </h3>
          <div className="relative w-full md:w-80 group">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-300 group-focus-within:text-primary transition-colors" size={16} />
            <input 
              placeholder="Pesquisar evento..." 
              className="w-full pl-11 pr-4 py-3 bg-slate-50 border border-slate-100 rounded-2xl outline-none font-bold text-xs focus:ring-4 focus:ring-primary/5 transition-all"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="bg-slate-50 text-slate-400 text-[9px] font-black uppercase tracking-widest border-b">
                <th className="px-8 py-5">Identificação</th>
                <th className="px-6 py-5 text-center">Faturamento</th>
                <th className="px-6 py-5 text-center">Custos</th>
                <th className="px-6 py-5 text-center">Eficiência</th>
                <th className="px-8 py-5 text-right">Ação</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {filteredOrders.map(o => {
                const approvedExpenses = o.expenses?.filter(e => e.status === ExpenseStatus.APROVADO).reduce((a,e) => a + e.amount, 0) || 0;
                const approvedExtras = o.extraItems?.filter(e => e.status === ExtraItemStatus.APROVADO).reduce((a,e) => a + (e.qty*e.value), 0) || 0;
                const totalRev = o.estimatedCost + approvedExtras;
                const profit = totalRev - approvedExpenses;
                const margin = totalRev > 0 ? (profit / totalRev) * 100 : 0;

                return (
                  <tr key={o.id} className="hover:bg-primary/[0.02] cursor-pointer group" onClick={() => setSelectedOrder(o)}>
                    <td className="px-8 py-6">
                      <p className="text-sm font-black text-secondary uppercase group-hover:text-primary transition-colors">{o.nomeEvento}</p>
                      <p className="text-[10px] text-gray-400 uppercase">ID: {o.id}</p>
                    </td>
                    <td className="px-6 py-6 text-center font-bold text-slate-700">R$ {totalRev.toLocaleString('pt-BR')}</td>
                    <td className="px-6 py-6 text-center text-red-500 font-bold">R$ {approvedExpenses.toLocaleString('pt-BR')}</td>
                    <td className="px-6 py-6 text-center font-black text-primary">{margin.toFixed(1)}%</td>
                    <td className="px-8 py-6 text-right">
                       <div className="flex items-center justify-end gap-2">
                          <ChevronRight size={20} className="text-gray-200 group-hover:text-primary group-hover:translate-x-1 transition-all" />
                       </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      {selectedOrder && (
        <ProfitabilityDossier 
          order={selectedOrder} 
          client={clients.find(c => c.id === selectedOrder.clientId)}
          onClose={() => setSelectedOrder(null)} 
          onUpdateOrder={handleUpdateSingleOrder}
        />
      )}
    </div>
  );
};

const ProfitabilityDossier = ({ order, client, onClose, onUpdateOrder }: any) => {
  const [activeView, setActiveView] = useState<'main' | 'new_revenue' | 'new_expense' | 'edit_order'>('main');
  const [formDesc, setFormDesc] = useState('');
  const [formVal, setFormVal] = useState('');
  const [isCopied, setIsCopied] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [editData, setEditData] = useState({
    nomeEvento: order.nomeEvento,
    dataEvento: order.dataEvento,
    diaRetirada: order.diaRetirada
  });

  const approvedExpenses = useMemo(() => order.expenses?.filter((e:any) => e.status === ExpenseStatus.APROVADO) || [], [order]);
  const approvedExtras = useMemo(() => order.extraItems?.filter((ex:any) => ex.status === ExtraItemStatus.APROVADO) || [], [order]);
  
  const totalExtras = approvedExtras.reduce((a:number, e:any) => a + (e.qty * e.value), 0);
  const totalRev = order.estimatedCost + totalExtras;
  const totalExp = approvedExpenses.reduce((a:number, e:any) => a + e.amount, 0);
  const netProfit = totalRev - totalExp;
  const marginPct = totalRev > 0 ? (netProfit / totalRev) * 100 : 0;

  const handleUpdatePhoto = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        onUpdateOrder({ ...order, eventPhoto: reader.result as string });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleCopyLink = () => {
    const link = `https://eventlog.pro/dossier/${order.id}`;
    navigator.clipboard.writeText(link);
    setIsCopied(true);
    setTimeout(() => setIsCopied(false), 2000);
  };

  const handleSaveOrderEdits = () => {
    onUpdateOrder({ ...order, ...editData });
    setActiveView('main');
  };

  const handleConfirmRegistration = () => {
    if (!formDesc || !formVal) return;
    const valueNum = parseFloat(formVal.replace(',', '.'));

    if (activeView === 'new_revenue') {
      const newExtra: ExtraItem = {
        id: `ex-${Date.now()}`,
        orderId: order.id,
        description: formDesc,
        qty: 1,
        value: valueNum,
        date: new Date().toISOString().split('T')[0],
        status: ExtraItemStatus.AGUARDANDO_CONTROLADORIA,
        requestedBy: 'Gestor (Ajuste)'
      };
      onUpdateOrder({ ...order, extraItems: [...(order.extraItems || []), newExtra] });
    } else {
      const newExpense: EventExpense = {
        id: `exp-${Date.now()}`,
        orderId: order.id,
        company_id: order.company_id || '',
        description: formDesc,
        amount: valueNum,
        category: 'OUTROS',
        date: new Date().toISOString().split('T')[0],
        status: ExpenseStatus.PENDENTE_APROVACAO,
        requestedBy: 'Gestor (Ajuste)'
      };
      onUpdateOrder({ ...order, expenses: [...(order.expenses || []), newExpense] });
    }

    setFormDesc('');
    setFormVal('');
    setActiveView('main');
  };

  const handleRequestItemDelete = (type: 'EXTRA' | 'EXPENSE', id: string) => {
    if (type === 'EXTRA') {
        const item = order.extraItems.find((e:any) => e.id === id);
        if (item.status === ExtraItemStatus.APROVADO) {
            onUpdateOrder({
                ...order,
                extraItems: order.extraItems.map((e:any) => e.id === id ? {...e, status: ExtraItemStatus.EXCLUSAO_PENDENTE} : e)
            });
            alert("Solicitação de exclusão enviada para auditoria.");
        } else {
            onUpdateOrder({
                ...order,
                extraItems: order.extraItems.filter((e:any) => e.id !== id)
            });
        }
    } else {
        const item = order.expenses.find((e:any) => e.id === id);
        if (item.status === ExpenseStatus.APROVADO) {
            onUpdateOrder({
                ...order,
                expenses: order.expenses.map((e:any) => e.id === id ? {...e, status: ExpenseStatus.EXCLUSAO_PENDENTE} : e)
            });
            alert("Solicitação de exclusão enviada para auditoria.");
        } else {
            onUpdateOrder({
                ...order,
                expenses: order.expenses.filter((e:any) => e.id !== id)
            });
        }
    }
  };

  const handlePrintReport = () => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) return alert("Bloqueador de pop-ups detectado.");

    const html = `
      <html>
        <head>
          <title>Financeiro do Evento - ${order.nomeEvento}</title>
          <style>
            body { font-family: sans-serif; padding: 40px; color: #334155; line-height: 1.5; }
            .header { border-bottom: 3px solid #00a8e0; padding-bottom: 20px; margin-bottom: 30px; display: flex; justify-content: space-between; align-items: center; }
            .title { font-size: 24pt; font-weight: 900; text-transform: uppercase; color: #1e293b; margin: 0; }
            .section { margin-bottom: 30px; }
            .section-title { font-size: 10pt; font-weight: 900; text-transform: uppercase; color: #00a8e0; letter-spacing: 2px; border-bottom: 1px solid #e2e8f0; padding-bottom: 5px; margin-bottom: 15px; }
            table { width: 100%; border-collapse: collapse; margin-top: 10px; }
            th { text-align: left; font-size: 9pt; text-transform: uppercase; color: #94a3b8; padding: 10px; border-bottom: 2px solid #f1f5f9; }
            td { padding: 12px 10px; font-size: 10pt; border-bottom: 1px solid #f1f5f9; }
            .val-res { font-weight: 900; text-align: right; }
            .summary-grid { display: grid; grid-template-cols: repeat(3, 1fr); gap: 20px; margin-bottom: 40px; }
            .summary-box { padding: 20px; border-radius: 15px; background: #f8fafc; border: 1px solid #e2e8f0; }
            .summary-label { font-size: 8pt; font-weight: 900; color: #64748b; text-transform: uppercase; }
            .summary-val { font-size: 16pt; font-weight: 900; color: #1e293b; margin-top: 5px; }
            .profit-box { background: #0f172a; color: white; }
            .profit-box .summary-label { color: #00a8e0; }
            .profit-box .summary-val { color: white; }
          </style>
        </head>
        <body>
          <div class="header">
             <div>
               <h1 class="title">Financeiro do Evento</h1>
               <p style="font-size: 10pt; font-weight: bold; margin-top: 5px;">EVENTO: ${order.nomeEvento.toUpperCase()}</p>
             </div>
             <div style="text-align: right; font-size: 9pt;">
               Gerado em: ${new Date().toLocaleString('pt-BR')}<br/>
               ID: ${order.id}
             </div>
          </div>

          <div class="summary-grid">
             <div class="summary-box">
                <div class="summary-label">Total Receitas</div>
                <div class="summary-val">R$ ${totalRev.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</div>
             </div>
             <div class="summary-box">
                <div class="summary-label">Total Custos</div>
                <div class="summary-val" style="color: #ef4444;">- R$ ${totalExp.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</div>
             </div>
             <div class="summary-box profit-box">
                <div class="summary-label">Lucro Líquido (${marginPct.toFixed(1)}%)</div>
                <div class="summary-val">R$ ${netProfit.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</div>
             </div>
          </div>

          <div class="section">
             <div class="section-title">Detalhamento de Entradas (Receitas)</div>
             <table>
                <thead>
                   <tr>
                      <th>Descrição</th>
                      <th>Data</th>
                      <th style="text-align: right;">Valor</th>
                   </tr>
                </thead>
                <tbody>
                   <tr><td>Faturamento Base O.S.</td><td>${new Date(order.createdAt).toLocaleDateString()}</td><td class="val-res">R$ ${order.estimatedCost.toLocaleString('pt-BR')}</td></tr>
                   ${approvedExtras.map((ex:any) => `
                     <tr><td>${ex.description} (Adicional)</td><td>${new Date(ex.date).toLocaleDateString()}</td><td class="val-res">R$ ${(ex.qty * ex.value).toLocaleString('pt-BR')}</td></tr>
                   `).join('')}
                </tbody>
             </table>
          </div>

          <div class="section">
             <div class="section-title">Detalhamento de Saídas (Custos de Campo)</div>
             <table>
                <thead>
                   <tr>
                      <th>Descrição / Categoria</th>
                      <th>Data</th>
                      <th style="text-align: right;">Valor</th>
                   </tr>
                </thead>
                <tbody>
                   ${approvedExpenses.length > 0 ? approvedExpenses.map((ex:any) => `
                     <tr><td>${ex.description} [${ex.category}]</td><td>${new Date(ex.date).toLocaleDateString()}</td><td class="val-res" style="color: #ef4444;">- R$ ${ex.amount.toLocaleString('pt-BR')}</td></tr>
                   `).join('') : '<tr><td colspan="3" style="text-align: center; color: #94a3b8; font-style: italic;">Sem despesas registradas</td></tr>'}
                </tbody>
             </table>
          </div>

          <div style="margin-top: 100px; text-align: center; font-size: 8pt; color: #94a3b8; border-top: 1px solid #f1f5f9; padding-top: 20px;">
             Relatório gerado automaticamente pelo Sistema EventLog PRO Core Engine.
          </div>
          <script>window.onload = function() { window.print(); window.onafterprint = function() { window.close(); }; }</script>
        </body>
      </html>
    `;
    printWindow.document.write(html);
    printWindow.document.close();
  };

  return (
    <div className="fixed inset-0 bg-secondary/80 backdrop-blur-md z-[2000] flex justify-end animate-in fade-in duration-300">
       <div className="bg-[#f8f9fa] h-full w-full max-w-5xl shadow-2xl flex flex-col animate-in slide-in-from-right duration-500 overflow-hidden border-l border-white/20">
          
          <div className="bg-white px-8 py-4 border-b border-gray-100 flex justify-between items-center shrink-0 shadow-sm z-20">
             <div className="flex gap-2">
                <button onClick={onClose} className="px-4 py-2 bg-primary text-white rounded-lg text-[10px] font-black uppercase tracking-widest shadow-lg shadow-primary/20 hover:brightness-110 transition-all">Ver Todos Eventos</button>
                <button onClick={() => setActiveView('edit_order')} className="px-4 py-2 bg-amber-50 text-secondary rounded-lg text-[10px] font-black uppercase tracking-widest shadow-lg shadow-amber-500/20 hover:brightness-110 transition-all active:scale-95 border border-amber-600/20">Editar</button>
                <button onClick={handleCopyLink} className="px-4 py-2 bg-cyan-500 text-white rounded-lg text-[10px] font-black uppercase tracking-widest shadow-lg shadow-cyan-500/20 hover:brightness-110 transition-all flex items-center gap-2 active:scale-95">
                   {isCopied ? <CheckIcon size={12} strokeWidth={4} /> : <Link size={12} />}
                   {isCopied ? 'Copiado!' : 'Link de Formulário'}
                </button>
             </div>
             <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-xl text-gray-400 transition-all active:scale-90"><X size={24} /></button>
          </div>

          <div className="flex-1 overflow-y-auto p-8 lg:p-12 scrollbar-hide">
             {activeView === 'main' ? (
               <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
                  <div className="lg:col-span-4 flex flex-col items-center text-center space-y-6">
                     <div className="relative group">
                        <div 
                          onClick={() => fileInputRef.current?.click()}
                          className="w-52 h-52 rounded-full border-8 border-white shadow-2xl overflow-hidden bg-gray-100 cursor-pointer relative"
                        >
                           <img 
                            src={order.eventPhoto || "https://images.unsplash.com/photo-1505373877841-8d25f7d46678?auto=format&fit=crop&w=400&q=80"} 
                            className="w-full h-full object-cover group-hover:brightness-50 transition-all" 
                            alt="Evento" 
                           />
                           <div className="absolute inset-0 flex flex-col items-center justify-center text-white opacity-0 group-hover:opacity-100 transition-opacity">
                              <Camera size={32} />
                              <span className="text-[10px] font-black uppercase mt-1 tracking-widest">Trocar Foto</span>
                           </div>
                        </div>
                        <input type="file" ref={fileInputRef} className="hidden" accept="image/*" onChange={handleUpdatePhoto} />
                        <div className="absolute -top-2 -right-2 p-4 bg-green-500 text-white rounded-3xl shadow-xl border-4 border-white">
                           <TrendingUp size={24} strokeWidth={3} />
                        </div>
                     </div>
                     <div>
                        <h2 className="text-3xl font-black text-secondary uppercase tracking-tight leading-none mb-2 italic">{order.nomeEvento}</h2>
                        <div className="flex flex-col items-center gap-2">
                           <div className="flex items-center gap-1.5 text-gray-400 font-bold uppercase text-[10px] tracking-widest"><Star size={12} className="text-amber-500" /> Corporativo</div>
                           <div className="flex items-center gap-1.5 text-gray-400 font-bold uppercase text-[10px] tracking-widest"><MapPin size={12} className="text-primary" /> {client?.nomeLocal || 'Local Indefinido'}</div>
                        </div>
                     </div>
                  </div>

                  <div className="lg:col-span-8 space-y-10">
                     <div className="flex items-center gap-3 border-b border-gray-200 pb-4">
                        <div className="p-2 bg-blue-50 rounded-lg text-primary"><BarChart3 size={20} /></div>
                        <h3 className="text-xl font-black text-secondary uppercase italic">Financeiro do Evento</h3>
                     </div>

                     <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <Card className="bg-white p-6 rounded-[28px] shadow-sm border border-gray-100 flex flex-col justify-between h-36">
                           <span className="text-[9px] font-black text-amber-600 uppercase tracking-[0.2em]">Contratado Base</span>
                           <div>
                              <p className="text-2xl font-black text-secondary tracking-tighter">R$ {order.estimatedCost.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</p>
                              <p className="text-[8px] text-gray-400 font-bold mt-1 uppercase">O.S. Original</p>
                           </div>
                        </Card>
                        <Card className="bg-white p-6 rounded-[28px] shadow-sm border border-gray-100 border-l-8 border-l-emerald-400 flex flex-col justify-between h-36">
                           <span className="text-[9px] font-black text-emerald-600 uppercase tracking-[0.2em]">Total Receitas</span>
                           <div>
                              <p className="text-2xl font-black text-secondary tracking-tighter">R$ {totalRev.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</p>
                              <p className="text-[8px] text-emerald-500 font-bold mt-1 uppercase flex items-center gap-1">Base + Adicionais <ArrowUpRight size={12} /></p>
                           </div>
                        </Card>
                        <Card className="bg-white p-6 rounded-[28px] shadow-sm border border-gray-100 border-l-8 border-l-red-500 flex flex-col justify-between h-36">
                           <span className="text-[9px] font-black text-red-600 uppercase tracking-[0.2em]">Total Despesas</span>
                           <div>
                              <p className="text-2xl font-black text-secondary tracking-tighter">R$ {totalExp.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</p>
                              <p className="text-[8px] text-red-400 font-bold mt-1 uppercase flex items-center gap-1">Custos Logísticos <ArrowDownLeft size={12} /></p>
                           </div>
                        </Card>
                     </div>

                     <div className="flex gap-3">
                        <button onClick={() => setActiveView('new_revenue')} className="flex-1 py-4 bg-emerald-500 text-white rounded-2xl font-black uppercase text-[10px] tracking-widest shadow-xl shadow-emerald-100 hover:brightness-110 transition-all flex items-center justify-center gap-2">
                           <Plus size={16} strokeWidth={3} /> Nova Receita
                        </button>
                        <button onClick={() => setActiveView('new_expense')} className="flex-1 py-4 bg-red-500 text-white rounded-2xl font-black uppercase text-[10px] tracking-widest shadow-xl shadow-red-100 hover:brightness-110 transition-all flex items-center justify-center gap-2">
                           <Plus size={16} strokeWidth={3} /> Nova Despesa
                        </button>
                     </div>

                     <div className="space-y-6">
                        <div className="flex justify-between items-end border-b pb-4 border-emerald-100">
                           <h4 className="text-[11px] font-black text-emerald-600 uppercase tracking-[0.3em] flex items-center gap-2 italic">
                              <PackagePlus size={16} /> Detalhamento de Adicionais
                           </h4>
                           <span className="text-[10px] font-black text-emerald-400 uppercase">Soma Extras: R$ {totalExtras.toLocaleString('pt-BR')}</span>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                           {(order.extraItems || []).filter((e:any) => e.status !== ExtraItemStatus.EXCLUSAO_PENDENTE).map((ex: any) => (
                              <div key={ex.id} className={`p-4 bg-white border rounded-2xl flex items-center justify-between group hover:shadow-md transition-all ${ex.status === ExtraItemStatus.APROVADO ? 'border-emerald-100' : 'border-gray-100 opacity-60'}`}>
                                 <div className="flex items-center gap-3">
                                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${ex.status === ExtraItemStatus.APROVADO ? 'bg-emerald-50 text-emerald-500' : 'bg-gray-50 text-gray-400'}`}>
                                       <Tag size={16} />
                                    </div>
                                    <div>
                                       <p className="text-[10px] font-black text-secondary uppercase leading-none">{ex.description}</p>
                                       <p className="text-[8px] text-gray-400 font-bold uppercase mt-1">Qtd: {ex.qty} • {ex.status}</p>
                                    </div>
                                 </div>
                                 <div className="flex items-center gap-2">
                                    <span className={`font-black text-xs ${ex.status === ExtraItemStatus.APROVADO ? 'text-emerald-600' : 'text-gray-400'}`}>
                                        + R$ {(ex.qty * ex.value).toLocaleString('pt-BR')}
                                    </span>
                                    <button 
                                        onClick={() => handleRequestItemDelete('EXTRA', ex.id)}
                                        className="p-1.5 text-gray-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-all"
                                    >
                                        <Trash2 size={14} />
                                    </button>
                                 </div>
                              </div>
                           ))}
                        </div>
                     </div>

                     <div className="space-y-6">
                        <div className="flex justify-between items-end border-b pb-4 border-red-100">
                           <h4 className="text-[11px] font-black text-red-500 uppercase tracking-[0.3em] flex items-center gap-2 italic">
                              <TrendingDown size={16} /> Detalhamento de Despesas
                           </h4>
                           <span className="text-[10px] font-black text-red-400 uppercase">Total Custos: R$ {totalExp.toLocaleString('pt-BR')}</span>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                           {(order.expenses || []).filter((e:any) => e.status !== ExpenseStatus.EXCLUSAO_PENDENTE).map((exp: any) => (
                              <div key={exp.id} className={`p-4 bg-white border rounded-2xl flex items-center justify-between group hover:shadow-md transition-all ${exp.status === ExpenseStatus.APROVADO ? 'border-red-100' : 'border-gray-100 opacity-60'}`}>
                                 <div className="flex items-center gap-3">
                                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${exp.status === ExpenseStatus.APROVADO ? 'bg-red-50 text-red-500' : 'bg-gray-50 text-gray-400'}`}>
                                       {exp.category === 'LOGISTICA' ? <Fuel size={16} /> : <Receipt size={16} />}
                                    </div>
                                    <div>
                                       <p className="text-[10px] font-black text-secondary uppercase leading-none">{exp.description}</p>
                                       <p className="text-[8px] text-red-400 font-bold uppercase mt-1">{exp.category} • {exp.status}</p>
                                    </div>
                                 </div>
                                 <div className="flex items-center gap-2">
                                    <span className={`font-black text-xs ${exp.status === ExpenseStatus.APROVADO ? 'text-red-600' : 'text-gray-400'}`}>
                                        - R$ {exp.amount.toLocaleString('pt-BR')}
                                    </span>
                                    <button 
                                        onClick={() => handleRequestItemDelete('EXPENSE', exp.id)}
                                        className="p-1.5 text-gray-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-all"
                                    >
                                        <Trash2 size={14} />
                                    </button>
                                 </div>
                              </div>
                           ))}
                        </div>
                     </div>
                  </div>
               </div>
             ) : activeView === 'edit_order' ? (
               <div className="max-w-2xl mx-auto py-10 animate-in slide-in-from-bottom-4">
                  <Card className="bg-white p-10 rounded-[50px] shadow-2xl border-4 border-amber-500/10">
                     <div className="flex justify-between items-center mb-10">
                        <div className="flex items-center gap-4">
                           <div className="p-4 rounded-2xl bg-amber-500 text-white shadow-xl">
                              <Edit3 size={24} />
                           </div>
                           <h3 className="text-2xl font-black uppercase text-secondary italic">Editar Dados do Evento</h3>
                        </div>
                        <button onClick={() => setActiveView('main')} className="p-3 bg-gray-50 rounded-2xl hover:bg-gray-100 transition-all"><X size={20} /></button>
                     </div>

                     <div className="space-y-6">
                        <div className="space-y-1.5">
                           <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-4">Nome do Evento</label>
                           <input 
                              className="w-full bg-gray-50 border border-gray-100 p-5 rounded-3xl outline-none font-bold text-secondary focus:ring-4 focus:ring-amber-500/5 uppercase"
                              value={editData.nomeEvento}
                              onChange={e => setEditData({...editData, nomeEvento: e.target.value})}
                           />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                           <div className="space-y-1.5">
                              <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-4">Data Início</label>
                              <input 
                                 type="date"
                                 className="w-full bg-gray-50 border border-gray-100 p-5 rounded-3xl outline-none font-bold text-secondary focus:ring-4 focus:ring-amber-500/5"
                                 value={editData.dataEvento}
                                 onChange={e => setEditData({...editData, dataEvento: e.target.value})}
                              />
                           </div>
                           <div className="space-y-1.5">
                              <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-4">Data Retirada</label>
                              <input 
                                 type="date"
                                 className="w-full bg-gray-50 border border-gray-100 p-5 rounded-3xl outline-none font-bold text-secondary focus:ring-4 focus:ring-amber-500/5"
                                 value={editData.diaRetirada}
                                 onChange={e => setEditData({...editData, diaRetirada: e.target.value})}
                              />
                           </div>
                        </div>
                        <div className="pt-8 flex gap-4">
                           <button onClick={() => setActiveView('main')} className="flex-1 py-5 rounded-3xl font-black uppercase text-[10px] text-gray-400 hover:bg-gray-50 transition-all">Cancelar</button>
                           <button onClick={handleSaveOrderEdits} className="flex-[2] py-5 bg-amber-500 text-secondary rounded-3xl font-black uppercase text-xs shadow-xl active:scale-95 transition-all flex items-center justify-center gap-2">
                              <Save size={18} /> Salvar Alterações
                           </button>
                        </div>
                     </div>
                  </Card>
               </div>
             ) : (
               <div className="max-w-xl mx-auto py-10 animate-in slide-in-from-bottom-4">
                  <Card className="bg-white p-10 rounded-[50px] shadow-2xl border-4 border-primary/10">
                     <div className="flex justify-between items-center mb-10">
                        <div className="flex items-center gap-4">
                           <div className={`p-4 rounded-2xl ${activeView === 'new_revenue' ? 'bg-emerald-500' : 'bg-red-500'} text-white shadow-xl`}>
                              {activeView === 'new_revenue' ? <DollarSign size={24} /> : <Receipt size={24} />}
                           </div>
                           <h3 className="text-2xl font-black uppercase text-secondary italic">
                              {activeView === 'new_revenue' ? 'Registrar Receita' : 'Registrar Despesa'}
                           </h3>
                        </div>
                        <button onClick={() => { setActiveView('main'); setFormDesc(''); setFormVal(''); }} className="p-3 bg-gray-50 rounded-2xl hover:bg-gray-100 transition-all"><X size={20} /></button>
                     </div>

                     <div className="space-y-6">
                        <div className="space-y-1.5">
                           <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-4">Descrição do Lançamento</label>
                           <input 
                              autoFocus 
                              className="w-full bg-gray-50 border border-gray-100 p-5 rounded-3xl outline-none font-bold text-secondary focus:ring-4 focus:ring-primary/5 shadow-inner uppercase" 
                              placeholder="Ex: Adicional de 5 Tendas" 
                              value={formDesc}
                              onChange={e => setFormDesc(e.target.value)}
                           />
                        </div>
                        <div className="space-y-1.5">
                           <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-4">Valor Bruto (R$)</label>
                           <input 
                              type="number" 
                              className="w-full bg-gray-50 border border-gray-100 p-6 rounded-3xl outline-none font-black text-secondary text-3xl shadow-inner focus:ring-4 focus:ring-primary/5" 
                              placeholder="0,00" 
                              value={formVal}
                              onChange={e => setFormVal(e.target.value)}
                           />
                        </div>
                        <div className="p-5 bg-blue-50 rounded-2xl border border-blue-100 flex items-center gap-3">
                           <ShieldAlert size={18} className="text-primary shrink-0" />
                           <p className="text-[9px] font-bold text-primary uppercase tracking-widest">Este lançamento passará por auditoria da controladoria antes de afetar o lucro real.</p>
                        </div>
                        <div className="pt-4 flex gap-4">
                           <button onClick={() => { setActiveView('main'); setFormDesc(''); setFormVal(''); }} className="flex-1 py-5 rounded-3xl font-black uppercase text-[10px] text-gray-400 hover:bg-gray-50 transition-all">Cancelar</button>
                           <button onClick={handleConfirmRegistration} className={`flex-[2] py-5 ${activeView === 'new_revenue' ? 'bg-emerald-500' : 'bg-red-500'} text-white rounded-3xl font-black uppercase text-xs shadow-xl active:scale-95 transition-all flex items-center justify-center gap-2`}>
                              <Save size={18} /> Confirmar Registro
                           </button>
                        </div>
                     </div>
                  </Card>
               </div>
             )}
          </div>

          <div className="p-8 border-t border-gray-100 bg-white flex justify-end shrink-0 shadow-[0_-10px_30px_rgba(0,0,0,0.03)] z-20">
             <button 
                onClick={handlePrintReport}
                className="bg-secondary text-white px-12 py-5 rounded-[24px] font-black uppercase text-xs tracking-[0.2em] shadow-2xl hover:bg-black transition-all flex items-center justify-center gap-3 active:scale-95"
             >
                <Printer size={20} strokeWidth={3} /> Gerar PDF do Evento
             </button>
          </div>
       </div>
    </div>
  );
};
